
import Vue from 'vue';
import { TranslateResult } from 'vue-i18n';
import { Component, Emit, Prop } from 'vue-property-decorator';
import { namespace } from 'vuex-class';

import BasicButton from '@/ui-components/BasicButton.vue';

import { CollectionType } from '@/enums/collection-type';
import { FilterHintEvents } from '@/enums/events';

import { TrackingHelper } from '@/helpers/tracking/tracking-helper';

import { isAnyFilterActive } from '@/helpers/filter-helper';
import { TrackingProviderPropertiesInterface } from '@/helpers/tracking/providers';
import { FilterCollection, FilterName, getDefaultFilterCollection } from '@/stores/filter.store';

const Filter = namespace('filter');
const Routing = namespace('routing');

export type EmptyStateContent = {
	headline: TranslateResult;
	text: TranslateResult;
	hasFewTitles?: boolean;
	hasNoTitles?: boolean;
};
@Component({
	name: 'TimelineEmptyState',
	components: {
		BasicButton,
	},
})
export default class TimelineEmptyState extends Vue {
	@Prop({ default: false }) hasNoTitles!: boolean;
	@Prop({ default: false }) isAtEndOfList!: boolean;

	@Filter.Getter currentFilters: FilterCollection;
	@Filter.Action reset: () => void;
	@Filter.Action resetFilters: (payload: { filterNames: FilterName[] }) => void;

	@Routing.Getter activeCollectionType: CollectionType;

	@Emit('onScrollToTop')
	private onScrollToTop() {}

	get ASSETS_DIR() {
		return `/${ASSETS_DIR}`;
	}

	get filterNames(): FilterName[] {
		if (this.activeCollectionType !== CollectionType.WATCHLIST) {
			return [];
		}

		return <FilterName[]>(
			Object.keys(getDefaultFilterCollection()).filter(
				filterName => !['content_types', 'inner_tab'].includes(filterName)
			)
		);
	}

	get hasFiltersActive(): boolean {
		const filters: FilterName[] = [...Object.keys(this.currentFilters)] as FilterName[];
		return isAnyFilterActive(filters, this.currentFilters);
	}

	get btnText(): TranslateResult {
		return this.hasFiltersActive ? this.$t('WEBAPP_CLEAR_FILTERS') : this.$t('WEBAPP_GO_TO_TOP');
	}

	get reelBackground() {
		return {
			'background-image': `url('/${ASSETS_DIR}/img/film-reel.png')`,
			'background-repeat': 'no-repeat',
			'background-position-x': 'center',
		};
	}

	get emptyStateContent(): EmptyStateContent {
		if (this.hasNoTitles) {
			return {
				headline: this.$t('WEBAPP_TIMELINE_NO_RESULTS_HEADLINE'),
				text: this.$t('WEBAPP_TIMELINE_NO_RESULTS_TEXT'),
				hasNoTitles: true,
			};
		} else if (this.isAtEndOfList) {
			return {
				headline: this.$t('WEBAPP_TIMELINE_END_OF_TIMELINE_HEADLINE'),
				text: this.$t('WEBAPP_TIMELINE_END_OF_TIMELINE_TEXT'),
				hasFewTitles: true,
			};
		} else {
			return { headline: '', text: '' };
		}
	}

	mounted() {
		this.trackEvent(FilterHintEvents.LOADED);
	}

	clicked() {
		const { filterNames, hasFiltersActive } = this;
		if (hasFiltersActive) {
			if (filterNames.length) {
				this.resetFilters({ filterNames });
			} else {
				this.reset();
			}
		} else {
			this.onScrollToTop();
		}
		// this.trackEvent(FilterHintEvents.CLICKED);
	}

	visibilityChanged(isVisible: boolean) {
		if (isVisible) {
			this.trackEvent(FilterHintEvents.SHOWN);
		}
	}

	trackEvent(action: FilterHintEvents): void {
		const label =
			(this.emptyStateContent.hasFewTitles && FilterHintEvents.NO_MORE_TITLES) ||
			(this.emptyStateContent.hasNoTitles && FilterHintEvents.NO_TITLES) ||
			'';

		const properties: TrackingProviderPropertiesInterface = { action, label };
		if ([FilterHintEvents.SHOWN, FilterHintEvents.LOADED].includes(action)) properties.nonInteraction = true;

		TrackingHelper.trackEvent('filter_hint', properties);
	}
}
